import Checkbox from "@mui/material/Checkbox";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MotorDataRow } from "../models/MotorDataRow";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { Report } from "@/utilities/export/report";
import { formatNumber } from "@/utilities/formatNumber";

/** Row height the grid is configured with, used to size the grid to its content. */
const ROW_HEIGHT = 52;

interface Props {
  rows: MotorDataRow[];
  loading: boolean;
  /** Header of the Excel export. */
  report: Omit<Report, "table">;
  exportDisabled: boolean;
  /** Diagnostics currently charted. */
  chartedIds: Set<string>;
  onChartedChange: (diagnosticId: string, charted: boolean) => void;
}

/**
 * The measurements of a vehicle, grouped by what they are about.
 *
 * The grid is sized to its content and the page scrolls, rather than the grid scrolling inside a
 * fixed height: the groups are the point of the table, and a reader comparing two of them should
 * not have to scroll a pane inside a pane.
 */
export function MotorDataGrid({
  rows,
  loading,
  report,
  exportDisabled,
  chartedIds,
  onChartedChange,
}: Props) {
  const { t, i18n } = useTranslation();

  const columns = useMemo<GridColDef<MotorDataRow>[]>(
    () => [
      {
        field: "chart",
        headerName: t("motorData.columns.chart"),
        width: 80,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: ({ row }: GridRenderCellParams<MotorDataRow>) => (
          <Checkbox
            size="small"
            checked={chartedIds.has(row.diagnosticId)}
            onChange={(_event, checked) => onChartedChange(row.diagnosticId, checked)}
            inputProps={{ "aria-label": t("motorData.columns.chart") }}
          />
        ),
      },
      {
        field: "latest",
        headerName: t("motorData.columns.latest"),
        width: 130,
        sortable: false,
      },
      { field: "unit", headerName: t("motorData.columns.unit"), width: 110 },
      {
        field: "min",
        headerName: t("motorData.columns.min"),
        type: "number",
        width: 120,
        valueFormatter: (value: number | null) =>
          formatNumber(value, i18n.language, { precision: 2 }),
      },
      {
        field: "max",
        headerName: t("motorData.columns.max"),
        type: "number",
        width: 120,
        valueFormatter: (value: number | null) =>
          formatNumber(value, i18n.language, { precision: 2 }),
      },
      {
        field: "average",
        headerName: t("motorData.columns.average"),
        type: "number",
        width: 120,
        valueFormatter: (value: number | null) =>
          formatNumber(value, i18n.language, { precision: 2 }),
      },
      {
        field: "count",
        headerName: t("motorData.columns.count"),
        type: "number",
        width: 110,
      },
      {
        field: "lastUpdated",
        headerName: t("motorData.columns.lastUpdated"),
        type: "dateTime",
        width: 170,
        valueFormatter: (value: Date | null) => formatDate(value ?? undefined, i18n.language),
      },
      { field: "source", headerName: t("motorData.columns.source"), width: 180 },
      {
        field: "diagnosticId",
        headerName: t("motorData.columns.diagnosticId"),
        width: 220,
        sortable: false,
      },
    ],
    [t, i18n.language, chartedIds, onChartedChange],
  );

  const height = useMemo(() => {
    const groups = new Set(rows.map(row => row.groupPath[0])).size;
    // Header and footer chrome plus a row per measurement and per group.
    return Math.max(320, 110 + (rows.length + groups) * ROW_HEIGHT);
  }, [rows]);

  return (
    <I18nGrid
      settingsKey="motorData"
      rows={rows}
      columns={columns}
      loading={loading}
      getRowId={row => row.id}
      treeData
      getTreeDataPath={row => row.groupPath}
      groupingColDef={{
        headerName: t("motorData.columns.metric"),
        width: 360,
      }}
      defaultGroupingExpansionDepth={-1}
      disableRowSelectionOnClick
      slots={{ toolbar: ExportToolbar }}
      slotProps={{
        toolbar: {
          report,
          fractionDigits: { min: 2, max: 2, average: 2 },
          exportDisabled,
          hideFilter: true,
          hideDensity: true,
        },
      }}
      initialState={{
        columns: { columnVisibilityModel: { diagnosticId: false } },
        pagination: { paginationModel: { pageSize: 100, page: 0 } },
      }}
      pageSizeOptions={[50, 100, 250]}
      sx={{
        // The grid defaults to filling its parent, which ignores an explicit height; pin it to
        // its own size so the page scrolls instead of the grid.
        flex: "0 0 auto",
        height,
        "& .MuiDataGrid-row--group > .MuiDataGrid-cell": {
          bgcolor: "action.selected",
          fontWeight: 700,
        },
        "& .MuiDataGrid-treeDataGroupingCell": { fontWeight: 700 },
      }}
    />
  );
}
